import { type RefObject, useRef } from "react";
import {
  type ChangeMeta,
  NumberFormatBase,
  type NumericFormatProps,
  useNumericFormat,
} from "react-number-format";

const ABBREVIATION_MULTIPLIERS: Record<string, number> = {
  k: 1_000,
  m: 1_000_000,
  b: 1_000_000_000,
  t: 1_000_000_000_000,
};

type AbbreviationNumericFormatProps = NumericFormatProps & {
  ref?: RefObject<HTMLInputElement | null>;
};

export const AbbreviationNumericFormat = ({
  ref: refFromProps,
  ...props
}: AbbreviationNumericFormatProps) => {
  const { removeFormatting, onValueChange, ...rest } = useNumericFormat(props);
  const inputRefFromHook = useRef<HTMLInputElement | null>(null);
  const inputRef = refFromProps ?? inputRefFromHook;
  const didExpand = useRef(false);

  const customRemoveFormatting = (
    inputValue: string,
    changeMeta?: ChangeMeta,
  ) => {
    const clean = inputValue.replace(/[^0-9.,kmbt]/gi, "");

    // To prevent validation error for yup.number() because "." is not a valid number in JS
    if (clean === ".") {
      return "0.";
    }

    const match = clean.match(/^([\d,.]*)([kmbt])$/i);

    if (match) {
      const numericPart = match[1].replace(/,/g, "");
      const expanded =
        parseFloat(numericPart || "0") *
        ABBREVIATION_MULTIPLIERS[match[2].toLowerCase()];
      didExpand.current = true;

      return String(expanded);
    }

    return removeFormatting?.(inputValue, changeMeta) ?? inputValue;
  };

  const customValueChange: NumericFormatProps["onValueChange"] = (
    values,
    sourceInfo,
  ) => {
    if (didExpand.current) {
      didExpand.current = false;
      // react-number-format defers cursor positioning via setTimeout(0) inside
      // setPatchedCaretPosition. Scheduling our own setTimeout(0) here (after
      // that call has already been queued) ensures we run last and win.
      setTimeout(() => {
        const input = inputRef.current;

        if (input) {
          input.setSelectionRange(input.value.length, input.value.length);
        }
      }, 0);
    }
    onValueChange?.(values, sourceInfo);
  };

  const customGetInputRef = (el: HTMLInputElement | null) => {
    inputRef.current = el;
  };

  return (
    <NumberFormatBase
      {...rest}
      getInputRef={customGetInputRef}
      onValueChange={customValueChange}
      removeFormatting={customRemoveFormatting}
    />
  );
};
